/** True when the user is asking to start a chapter variation workflow. */
export function isChapterVariationRequest(text: string): boolean {
  const message = text.trim().toLowerCase();
  if (!message) return false;
  return (
    /\b(variation|rewrite|re-?write|alternate version|alternative version|new draft|revised version|new version)\b/.test(
      message
    ) || /\bgenerate a\b.*\b(chapter|version)\b/.test(message)
  );
}

/** True when the author is telling the assistant to produce the variation now. */
export function isVariationReadyToGenerate(text: string): boolean {
  const message = text.trim().toLowerCase();
  if (!message) return false;
  return (
    /\b(go ahead|proceed|generate it|generate the variation|create the variation|write it|do it now|make the variation|start the rewrite|that's correct|that is correct|looks good|ready to generate)\b/.test(
      message
    ) ||
    /\b(create|generate|write)\b.*\b(variation|rewrite|version)\b/.test(message)
  );
}

export const CHAPTER_VARIATION_FORMATTING_RULE =
  "Manuscript structure: separate paragraphs with one blank line (two newlines). Keep each quoted speech as one continuous paragraph — never insert line breaks or paragraph breaks in the middle of dialogue. Only split a single speaker's quotation across paragraphs when that speech is long (about 50+ words), the break falls after a complete sentence, and you follow multi-paragraph dialogue convention (no closing quote until the final paragraph; reopen with a quote on each continued paragraph). Apply formatting-only fixes (paragraph breaks, line breaks within quotes, whitespace) when the author asks, without rewriting the underlying words.";

export const CHAPTER_VARIATION_DIALOGUE_RULE =
  "Do not edit or tighten dialogue wording unless the author explicitly asks for dialogue rewrites. Inside quoted dialogue, allowed changes are spelling fixes, punctuation fixes, and formatting fixes (e.g. merging erroneous line breaks within a quote). Preserve what characters say.";

export const CHAPTER_VARIATION_FORMATTING_FOCUS_HINT =
  "Formatting-only rewrite: apply the author's formatting instructions (paragraph breaks, line breaks within quotes, whitespace). Merge any mid-dialogue line breaks so short quoted speech stays one paragraph. Keep the same words and sentence order except typos or punctuation. Do not rewrite, tighten, or reword unrelated prose.";

/** True when the author wants structural/formatting fixes rather than a prose rewrite. */
export function isFormattingFocusVariationRequest(text: string): boolean {
  const message = text.trim().toLowerCase();
  if (!message) return false;
  if (/\b(formatting|format fix|fix format|line break|line breaks|paragraph break|whitespace)\b/.test(message)) {
    return true;
  }
  if (
    /\b(dialogue|quoted|quotes|speech)\b/.test(message) &&
    /\b(line break|line breaks|split|separat|newline|new line)\b/.test(message)
  ) {
    return true;
  }
  if (/\b(not|no|don't|avoid|stop)\b.*\b(separat|split|break).*\b(line|paragraph)/.test(message)) {
    return true;
  }
  return false;
}

export const CHAPTER_VARIATION_START_PROMPT =
  "I want a variation of this chapter. Before you rewrite anything, ask me clarifying questions — especially proper names and spelling, schools and places, character details, and anything that must stay consistent.";

export const CHAPTER_VARIATION_GENERATE_PROMPT =
  "Generate the chapter variation now. Apply every clarification and spelling decision from our conversation.";

export const CHAPTER_VARIATION_CHAT_HINT = `Chapter variation workflow (when the author wants a variation or rewrite):
1. Clarify first — ask questions in chat about proper names and spelling, schools/places/characters, POV, and what must not change. Do not call create_chapter_variation during this phase.
2. Generate only when the author confirms they are ready (e.g. "go ahead", "generate the variation", "that's correct, proceed") or explicitly asks you to write/create the variation after answering your questions.
3. When calling create_chapter_variation, rewritten_content must be the complete rewritten chapter prose only — never acknowledgements, plans, or bullet lists.
4. ${CHAPTER_VARIATION_DIALOGUE_RULE}
5. ${CHAPTER_VARIATION_FORMATTING_RULE} When the author asks for formatting fixes (e.g. dialogue split across line breaks), apply those structural changes without rewording.
6. After the tool succeeds, reply briefly summarizing what changed; do not paste the full rewrite in chat.`;
